// Aplicación del "saldo a favor" (sobrepago) a los períodos pendientes del MISMO
// holder (propietario/inquilino/sintético unit_<id>), más antiguos primero.
//
// Modelo (decisión del usuario, "saldo como pool aparte"):
//  - Un sobrepago deja el excedente como saldo a favor (remainingAmount < 0). Bajo
//    este modelo el excedente NO se acredita a los fondos (ver fundBalances.js /
//    fundMovements.js: ratio topeado a 1). Queda como crédito de la unidad.
//  - Ese crédito cubre automáticamente los cargos del mes: el pago pendiente se marca
//    pagado/parcial y se anota cuánto se cubrió en `paidFromCredit`. Al marcarse
//    pagado, su desglose (GC/FR/fondos) recién ahí entra a los fondos (una sola vez).
//
// Idempotente: el crédito DISPONIBLE = Σ sobrepagos − Σ paidFromCredit ya aplicado.
// Re-ejecutar no vuelve a cubrir lo ya cubierto; borrar el pago cubierto restaura el
// crédito (no se toca el pago del sobrepago original).
//
// Solo cuenta el sobrepago de pagos VERIFICADOS. Lo que el residente informa queda
// en `pendiente_verificacion` con el monto que él declaró (las reglas solo exigen
// que cuadre la aritmética): si ese excedente contara como saldo a favor, un pago
// de 100 informado como 1.000.000 cubría en el próximo cierre todos sus meses
// pendientes, y rechazarlo después no revertía lo ya aplicado. Tampoco cuenta un
// pago rechazado. Lo ya consumido (paidFromCredit) se descuenta siempre, esté como
// esté el pago que lo consumió.
//
// Pesos enteros (30-oct-2026): el saldo a favor disponible se redondea a pesos por
// holder (un sobrepago de 0,48 no es saldo a favor) y un pago al que le quedan
// menos de 50 centésimos se da por saldado. Ver redondeoPesos.js.

import { roundPesos } from "./redondeoPesos";

const remOf = (p) =>
  p.remainingAmount != null
    ? Number(p.remainingAmount)
    : (Number(p.amount) || 0) - (Number(p.paidAmount) || 0);

const ESTADOS_SIN_VERIFICAR = new Set(["pendiente_verificacion", "rechazado"]);

/** true si el pago está verificado: ni informado sin revisar ni rechazado. */
export const esPagoVerificado = (p) => !ESTADOS_SIN_VERIFICAR.has(p?.status);

/**
 * Sobrepago (excedente ≥ 0) de un pago que cuenta como saldo a favor: solo el de
 * pagos verificados. Mismo criterio en el cierre (acá) y en Cobranza.
 */
export function sobrepagoAcreditable(p) {
  if (!p || !esPagoVerificado(p)) return 0;
  const r = remOf(p);
  return r < 0 ? -r : 0;
}

// Holder = quién es responsable de ese pago. Une el crédito con la deuda del MISMO
// ocupante (mismo userid: propietario, inquilino, o sintético unit_<id> de la unidad
// sin propietario). No cruza propietario ≠ inquilino porque tienen userid distinto.
// Se matchea por userid (no por apartmentId) para tolerar pagos donde el campo de
// unidad viene como apartmentId en unos y unitId/ausente en otros.
const keyOf = (p) => String(p.userid);

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Crédito (saldo a favor) DISPONIBLE por holder: Σ sobrepagos verificados
 * (remaining < 0) menos Σ paidFromCredit ya consumido, en pesos enteros.
 * Devuelve Map(key -> monto ≥ 0).
 */
export function availableCreditByHolder(payments) {
  const map = new Map();
  for (const p of payments || []) {
    const k = keyOf(p);
    const credit = sobrepagoAcreditable(p);         // sobrepago verificado (excedente)
    const applied = Number(p.paidFromCredit) || 0;  // ya consumido en pagos cubiertos
    map.set(k, (map.get(k) || 0) + credit - applied);
  }
  for (const [k, v] of map) map.set(k, Math.max(0, roundPesos(v)));
  return map;
}

/**
 * Calcula qué pagos PENDIENTES cubrir con el saldo a favor del mismo holder
 * (más antiguos primero). No muta nada: devuelve la lista de updates a persistir.
 *
 * @param {Array} payments  todos los pagos (del edificio o de la unidad)
 * @returns {Array<{id, paidFromCredit, paidAmount, remainingAmount, paid, status, appliedNow}>}
 */
export function computeCreditApplications(payments) {
  const avail = availableCreditByHolder(payments);

  const pendingByHolder = new Map();
  for (const p of payments || []) {
    if (p.paid) continue;
    if (p.status === "rechazado" || p.status === "pendiente_verificacion") continue;
    if (roundPesos(remOf(p)) <= 0) continue;
    const k = keyOf(p);
    if (!pendingByHolder.has(k)) pendingByHolder.set(k, []);
    pendingByHolder.get(k).push(p);
  }

  const updates = [];
  for (const [k, list] of pendingByHolder) {
    let pool = avail.get(k) || 0;
    if (pool <= 0) continue;
    list.sort((a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month));
    for (const p of list) {
      if (pool <= 0) break;
      const rem = remOf(p);
      const applied = Math.min(pool, rem);
      if (applied <= 0.005) continue;
      const newRemaining = round2(rem - applied);
      // Lo que queda por debajo de 50 centésimos no es deuda: el pago queda saldado.
      const fullyPaid = roundPesos(newRemaining) <= 0;
      updates.push({
        id: p.id,
        paidFromCredit: round2((Number(p.paidFromCredit) || 0) + applied),
        paidAmount: round2((Number(p.paidAmount) || 0) + applied),
        remainingAmount: fullyPaid ? 0 : newRemaining,
        paid: fullyPaid,
        status: fullyPaid ? "aprobado" : (p.status || "parcial"),
        appliedNow: round2(applied),
      });
      // El pool queda en pesos enteros: cubrir un pago viejo de 4.073,56 consume 4.074.
      pool = round2(pool - (fullyPaid ? roundPesos(applied) : applied));
    }
  }
  return updates;
}

export const _internals = { remOf, keyOf };
